import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getRemainingMessages, getMessageLimit, resetUsage, getRateLimitResetTime, setUserPlan, getRemainingProModelMessages, getProModelsResetInfo } from '../utils/rateLimit';
import { UserPlan } from '../types';
import SunIcon from '@heroicons/react/24/outline/SunIcon';
import MoonIcon from '@heroicons/react/24/outline/MoonIcon';
import ComputerDesktopIcon from '@heroicons/react/24/outline/ComputerDesktopIcon';
import { loadStripe } from '@stripe/stripe-js';

interface SettingsProps {
    isOpen: boolean;
    onClose: () => void;
    username: string;
    onUsernameChange: (username: string) => void;
    modelBehavior: string;
    onModelBehaviorChange: (behavior: string) => void;
    userPlan: UserPlan;
    onPlanChange: () => void;
    currentTheme: 'light' | 'dark' | 'system';
    onToggleTheme: () => void;
}

const Settings: React.FC<SettingsProps> = ({
    isOpen,
    onClose,
    username,
    onUsernameChange,
    modelBehavior,
    onModelBehaviorChange,
    userPlan,
    onPlanChange,
    currentTheme,
    onToggleTheme
}) => {
    const { currentUser, signOut } = useAuth();
    const [localUsername, setLocalUsername] = useState(username);
    const [localBehavior, setLocalBehavior] = useState(modelBehavior);
    const [remainingMessages, setRemainingMessages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isUpgrading, setIsUpgrading] = useState(false);
    const [showResetSuccess, setShowResetSuccess] = useState(false);
    const [resetTime, setResetTime] = useState(getRateLimitResetTime());
    const messageLimit = getMessageLimit(userPlan);
    const [remainingProMessages, setRemainingProMessages] = useState(0);
    const [proModelResetInfo, setProModelResetInfo] = useState(getProModelsResetInfo());

    useEffect(() => {
        setLocalUsername(username);
        setLocalBehavior(modelBehavior);
    }, [username, modelBehavior]);

    // Fetch remaining messages when the settings modal is opened
    useEffect(() => {
        if (!isOpen || !currentUser?.uid) return;

        const fetchRemainingMessages = async () => {
            try {
                setLoading(true);
                const remaining = await getRemainingMessages(currentUser.uid);
                setRemainingMessages(remaining);

                // Fetch Pro model messages if user is on Pro plan
                if (userPlan === 'pro') {
                    const proRemaining = await getRemainingProModelMessages(currentUser.uid);
                    setRemainingProMessages(proRemaining);
                    setProModelResetInfo(getProModelsResetInfo());
                }
            } catch (error) {
                console.error('Error fetching remaining messages:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchRemainingMessages();
        setResetTime(getRateLimitResetTime());
    }, [currentUser?.uid, isOpen, userPlan]);

    // Update reset time every minute when modal is open
    useEffect(() => {
        if (!isOpen) return;

        const intervalId = setInterval(() => {
            setResetTime(getRateLimitResetTime());
        }, 60000);

        return () => clearInterval(intervalId);
    }, [isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onUsernameChange(localUsername);
        onModelBehaviorChange(localBehavior);
        onClose();
    };

    const handleResetUsage = async () => {
        if (!currentUser?.uid) return;
        try {
            setLoading(true);
            await resetUsage(currentUser.uid);
            const remaining = await getRemainingMessages(currentUser.uid);
            setRemainingMessages(remaining);

            // Refresh Pro model count if user is on Pro plan
            if (userPlan === 'pro') {
                const proRemaining = await getRemainingProModelMessages(currentUser.uid);
                setRemainingProMessages(proRemaining);
                setProModelResetInfo(getProModelsResetInfo());
            }

            setShowResetSuccess(true);
            setTimeout(() => setShowResetSuccess(false), 3000);
        } catch (error) {
            console.error('Error resetting usage limit:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleUpgrade = async () => {
        if (!currentUser?.uid) return;
        setIsUpgrading(true);
        try {
            await setUserPlan(currentUser.uid, 'pro');
            onPlanChange();
        } catch (error) {
            console.error("Upgrade failed:", error);
            alert("Failed to upgrade plan. Please try again.");
        } finally {
            setIsUpgrading(false);
        }
    };

    const handleStripeCheckout = async () => {
        if (!currentUser?.uid) return;
        setIsUpgrading(true);
        try {
            // Call your backend API to create a Stripe checkout session
            const response = await fetch('/api/create-checkout-session', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    priceId: 'price_monthly_pro_plan', // This would be your actual Stripe price ID
                    returnUrl: window.location.origin + '/settings',
                }),
            });

            if (!response.ok) throw new Error('Failed to create checkout session');

            const { sessionId } = await response.json();

            // Load Stripe and redirect to checkout
            const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
            if (!stripe) throw new Error('Failed to load Stripe');

            await stripe.redirectToCheckout({ sessionId });
        } catch (error) {
            console.error("Checkout failed:", error);
            alert("Failed to start checkout process. Please try again.");
        } finally {
            setIsUpgrading(false);
        }
    };

    const handleManageSubscription = async () => {
        if (!currentUser?.uid) return;
        try {
            // Call your backend API to create a Stripe customer portal session
            const response = await fetch('/api/create-customer-portal', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: currentUser.uid,
                    returnUrl: window.location.origin + '/settings',
                }),
            });

            if (!response.ok) throw new Error('Failed to create customer portal session');

            const { url } = await response.json();

            // Redirect to the Stripe customer portal
            window.location.href = url;
        } catch (error) {
            console.error("Failed to open subscription management:", error);
            alert("Failed to open subscription management. Please try again.");
        }
    };

    // --- Integrate MessageUsage Logic --- 
    const usedMessages = messageLimit - remainingMessages;
    const percentage = Math.min(100, messageLimit > 0 ? (usedMessages / messageLimit) * 100 : 0);

    let progressBarColor = "bg-green-500";
    if (percentage > 70) progressBarColor = "bg-yellow-500";
    if (percentage >= 100) progressBarColor = "bg-red-500"; // Change to >= 100 for full red bar
    // --- End Integration ---

    // Calculate Pro model usage percentage
    const PRO_MODEL_LIMIT = 200; // Monthly limit for Pro models
    const usedProMessages = PRO_MODEL_LIMIT - remainingProMessages;
    const proPercentage = Math.min(100, PRO_MODEL_LIMIT > 0 ? (usedProMessages / PRO_MODEL_LIMIT) * 100 : 0);

    let proProgressBarColor = "bg-green-500";
    if (proPercentage > 70) proProgressBarColor = "bg-yellow-500";
    if (proPercentage >= 90) proProgressBarColor = "bg-red-500";

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
            {/* Apply theme styles to the modal background and border */}
            <div className="bg-background-primary dark:bg-dark-background-primary border border-glass-border dark:border-dark-glass-border rounded-xl shadow-lg max-w-xl w-full mx-4 overflow-hidden">
                {/* Apply theme styles to header */}
                <div className="flex justify-between items-center p-4 border-b border-glass-border dark:border-dark-glass-border">
                    <h2 className="text-xl font-semibold text-text-primary dark:text-dark-text-primary">Settings</h2>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg text-text-secondary dark:text-dark-text-secondary hover:bg-background-tertiary dark:hover:bg-dark-background-tertiary transition-colors"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[80vh] overflow-y-auto">
                    {/* Username Input - Apply theme styles */}
                    <div className="space-y-2">
                        <label htmlFor="username" className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary">
                            What should Versetaille call you?
                        </label>
                        <input
                            type="text"
                            id="username"
                            value={localUsername}
                            onChange={(e) => setLocalUsername(e.target.value)}
                            className="w-full bg-background-input dark:bg-dark-background-input border border-glass-border dark:border-dark-glass-border rounded-md py-2 px-3 text-text-primary dark:text-dark-text-primary focus:outline-none focus:border-primary dark:focus:border-dark-primary focus:ring-1 focus:ring-primary dark:focus:ring-dark-primary"
                            placeholder="Your name"
                        />
                    </div>

                    {/* AI Behavior Select - Apply theme styles */}
                    <div className="space-y-2">
                        <label htmlFor="behavior" className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary">
                            AI Behavior
                        </label>
                        <select
                            id="behavior"
                            value={localBehavior}
                            onChange={(e) => setLocalBehavior(e.target.value)}
                            className="w-full bg-background-input dark:bg-dark-background-input border border-glass-border dark:border-dark-glass-border rounded-md py-2 px-3 text-text-primary dark:text-dark-text-primary focus:outline-none focus:border-primary dark:focus:border-dark-primary focus:ring-1 focus:ring-primary dark:focus:ring-dark-primary"
                        >
                            <option value="standard">Standard</option>
                            <option value="creative">Creative</option>
                            <option value="precise">Precise</option>
                            <option value="friendly">Friendly</option>
                            <option value="professional">Professional</option>
                        </select>
                    </div>

                    {/* Theme Toggle Section */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary">
                            Appearance
                        </label>
                        <div className="bg-background-tertiary dark:bg-dark-background-tertiary border border-glass-border dark:border-dark-glass-border rounded-md p-3 flex justify-between items-center">
                            <span className="text-text-primary dark:text-dark-text-primary">
                                Theme:
                                <span className="ml-2 font-medium">
                                    {currentTheme === 'light' ? 'Light' :
                                        currentTheme === 'dark' ? 'Dark' : 'System'}
                                </span>
                            </span>
                            <button
                                type="button"
                                onClick={onToggleTheme}
                                className="p-2 rounded-lg hover:bg-background-secondary dark:hover:bg-dark-background-secondary transition-colors text-text-secondary dark:text-dark-text-secondary"
                                aria-label="Toggle theme mode"
                            >
                                {currentTheme === 'light' && (
                                    <MoonIcon className="h-5 w-5" />
                                )}
                                {currentTheme === 'dark' && (
                                    <SunIcon className="h-5 w-5" />
                                )}
                                {currentTheme === 'system' && (
                                    <ComputerDesktopIcon className="h-5 w-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Current Plan - Update the text */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary">Current Plan</label>
                        <div className="bg-background-tertiary dark:bg-dark-background-tertiary border border-glass-border dark:border-dark-glass-border rounded-md p-3 flex justify-between items-center">
                            <div>
                                <span className={`font-semibold ${userPlan === 'pro' ? 'text-purple-500 dark:text-purple-400' : userPlan === 'plus' ? 'text-blue-500 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
                                    {userPlan === 'pro' ? 'Pro Plan' : userPlan === 'plus' ? 'Plus Plan' : 'Free Plan'}
                                </span>
                                <p className="text-xs text-text-secondary dark:text-dark-text-secondary mt-1">
                                    {userPlan === 'pro'
                                        ? '200 Pro model messages per month, Unlimited Plus model usage'
                                        : userPlan === 'plus'
                                            ? 'Unlimited Plus model messages, 10 Free model messages per day'
                                            : '10 Free model messages per day'
                                    }
                                </p>
                            </div>
                            {userPlan === 'free' && (
                                <button
                                    type="button"
                                    onClick={handleUpgrade}
                                    disabled={isUpgrading}
                                    className={`px-3 py-1 text-xs bg-purple-500/10 hover:bg-purple-500/20 text-purple-600 dark:bg-purple-500/20 dark:hover:bg-purple-500/30 dark:text-purple-400 rounded-md transition-colors ${isUpgrading ? 'opacity-50 cursor-wait' : ''}`}
                                >
                                    {isUpgrading ? 'Upgrading...' : 'Upgrade to Pro'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Add Pro Model Usage Panel for Pro users */}
                    {userPlan === 'pro' && (
                        <div className="space-y-2 mt-4">
                            <label className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary">
                                Pro Model Usage (Monthly)
                            </label>
                            <div className="bg-background-tertiary dark:bg-dark-background-tertiary border border-glass-border dark:border-dark-glass-border rounded-md p-3">
                                {loading ? (
                                    <div className="flex items-center justify-center py-2 text-text-secondary dark:text-dark-text-secondary">
                                        <div className="w-5 h-5 border-2 border-purple-400 dark:border-purple-500 border-t-transparent rounded-full animate-spin"></div>
                                        <span className="ml-2 text-sm">Loading Pro Usage...</span>
                                    </div>
                                ) : (
                                    <div>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <div className="font-medium text-text-primary dark:text-dark-text-primary">Pro Model Message Usage</div>
                                                <div className="text-sm text-text-secondary dark:text-dark-text-secondary">
                                                    {usedProMessages}/{PRO_MODEL_LIMIT} used this month
                                                </div>
                                            </div>
                                            <div className="text-xs text-text-secondary dark:text-dark-text-secondary">
                                                Resets on {proModelResetInfo.formattedDate} (in {proModelResetInfo.daysUntil} days)
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-1">
                                                <div
                                                    className={`${proProgressBarColor} h-2.5 rounded-full transition-all duration-300`}
                                                    style={{ width: `${proPercentage}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                        <div className="mt-2 text-xs text-text-secondary dark:text-dark-text-secondary">
                                            <p>Pro models: <span className="font-medium text-purple-500 dark:text-purple-400">Claude 3.7 Sonnet, GPT-4.1, O4-Mini, Gemini 2.5 Pro</span></p>
                                            <p className="mt-1">All Plus and Free models have unlimited usage with your Pro plan</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Usage Limits - Change title to "Daily Usage Limits" to clarify */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary">
                            {userPlan === 'pro' ? 'Free Model Usage (Daily)' : 'Daily Usage Limits'}
                        </label>
                        <div className="bg-background-tertiary dark:bg-dark-background-tertiary border border-glass-border dark:border-dark-glass-border rounded-md p-3">
                            {loading ? (
                                <div className="flex items-center justify-center py-2 text-text-secondary dark:text-dark-text-secondary">
                                    {/* Adjust spinner color? */}
                                    <div className="w-5 h-5 border-2 border-blue-400 dark:border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                    <span className="ml-2 text-sm">Loading Usage...</span>
                                </div>
                            ) : (
                                <div>
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <div className="font-medium text-text-primary dark:text-dark-text-primary">Daily Message Usage ({userPlan === 'pro' ? 'Pro Plan' : 'Free Plan'})</div>
                                            <div className="text-sm text-text-secondary dark:text-dark-text-secondary">
                                                {usedMessages}/{messageLimit} used today
                                            </div>
                                        </div>
                                        <div className="text-xs text-text-secondary dark:text-dark-text-secondary">
                                            Resets at {resetTime.formattedTime} (in {resetTime.hoursUntil}h {resetTime.minutesUntil}m)
                                        </div>
                                    </div>
                                    <div className="mt-4">
                                        {/* Progress Bar - Apply theme styles */}
                                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-1">
                                            <div
                                                className={`${progressBarColor} h-2.5 rounded-full transition-all duration-300`}
                                                style={{ width: `${percentage}%` }}
                                            ></div>
                                        </div>
                                        <div className="text-xs text-text-secondary dark:text-dark-text-secondary">
                                            Resets at {resetTime.formattedTime} (in {resetTime.hoursUntil}h {resetTime.minutesUntil}m)
                                        </div>
                                    </div>
                                    <div className="mt-3 text-right border-t border-glass-border dark:border-dark-glass-border pt-3">
                                        <button
                                            type="button"
                                            onClick={handleResetUsage}
                                            disabled={loading || isUpgrading}
                                            className={`px-3 py-1 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:bg-blue-500/20 dark:hover:bg-blue-500/30 dark:text-blue-400 rounded-md transition-colors ${(loading || isUpgrading) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            Reset Usage Count
                                        </button>
                                        {showResetSuccess && (
                                            <div className="mt-1 text-xs text-green-600 dark:text-green-400 text-right inline-block ml-2">
                                                Usage count reset successfully.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Subscription Section */}
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">Pro Subscription</h3>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Subscribe to our Pro plan to unlock all premium features.
                        </p>
                        <div className="mt-3 bg-background-tertiary dark:bg-dark-background-tertiary border border-glass-border dark:border-dark-glass-border rounded-md p-3">
                            <div className="flex justify-between items-center">
                                <div>
                                    <span className="text-xl font-bold text-purple-500 dark:text-purple-400">$11.99</span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400"> / month</span>
                                    <p className="text-xs text-text-secondary dark:text-dark-text-secondary mt-1">
                                        Cancel anytime. Unlimited access to all premium features.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleStripeCheckout}
                                    disabled={isUpgrading || userPlan === 'pro'}
                                    className={`px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-md transition-colors ${(isUpgrading || userPlan === 'pro') ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {isUpgrading ? 'Processing...' : userPlan === 'pro' ? 'Current Plan' : 'Subscribe Now'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Optional: Current subscription details for pro users */}
                    {userPlan === 'pro' && (
                        <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                            <div className="flex items-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-500 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm text-green-700 dark:text-green-400">You are currently subscribed to the Pro plan.</span>
                            </div>
                            <div className="mt-2 flex justify-end">
                                <button
                                    type="button"
                                    onClick={handleManageSubscription}
                                    className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                                >
                                    Manage Subscription
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Account Section - Apply theme styles */}
                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-text-secondary dark:text-dark-text-secondary">
                            Account
                        </label>
                        <div className="bg-background-tertiary dark:bg-dark-background-tertiary border border-glass-border dark:border-dark-glass-border rounded-md p-3">
                            <div className="flex items-center space-x-3">
                                <img
                                    src={currentUser?.photoURL || "https://via.placeholder.com/40"}
                                    alt="Profile"
                                    className="w-10 h-10 rounded-full"
                                    referrerPolicy="no-referrer"
                                />
                                <div>
                                    <div className="font-medium text-text-primary dark:text-dark-text-primary">{currentUser?.displayName}</div>
                                    <div className="text-sm text-text-secondary dark:text-dark-text-secondary">{currentUser?.email}</div>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={signOut}
                                className="mt-3 w-full px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:bg-red-500/20 dark:hover:bg-red-500/30 dark:text-red-500 rounded-md transition-colors"
                            >
                                Sign Out
                            </button>
                        </div>
                    </div>

                    {/* Action Buttons - Apply theme styles */}
                    <div className="pt-2 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-glass-border dark:border-dark-glass-border rounded-md text-text-secondary dark:text-dark-text-secondary hover:bg-background-tertiary dark:hover:bg-dark-background-tertiary transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-primary/10 hover:bg-primary/20 text-primary dark:bg-dark-primary/20 dark:hover:bg-dark-primary/30 dark:text-dark-primary border border-primary/20 dark:border-dark-primary/30 rounded-md transition-colors"
                        >
                            Save Changes
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Settings;